import React from "react";
import { SKIP_ONBOARDING_STATUS_CHECK } from "@/constants";
import { Business, UserAggregate } from "@norma-bi/bi-api";
import { isAllBusiness } from "@/api";
import { useGetBusinesses } from "./useGetBusinesses";
import { OnboardingStatusPerBusiness, useOnboardingProgress } from "@/onboardingStatus";
import { useGetLicensingFeatureFlag } from "./useGetLicensingFeatureFlag";
import { useSelf } from "./useSelf";

export type BusinessStatus =
  | "unlicensed"
  | "onboarding"
  | "installation_failed"
  | "status_error"
  | "undetermined"
  | "available";

function getBusinessStatus(
  licensingEnabled: boolean,
  business: Business,
  onboardingStatusPerBusiness: OnboardingStatusPerBusiness,
  businesses: Business[],
  user: UserAggregate,
): BusinessStatus {
  if (!SKIP_ONBOARDING_STATUS_CHECK) {
    if (!onboardingStatusPerBusiness) {
      return "undetermined";
    } else if (isAllBusiness(business.id)) {
      if (Object.values(onboardingStatusPerBusiness).includes("check_error")) return "status_error";
      if (Object.values(onboardingStatusPerBusiness).includes("failed"))
        return "installation_failed";
      if (!Object.values(onboardingStatusPerBusiness).every((status) => status === "completed")) {
        return "onboarding";
      }
    } else if (onboardingStatusPerBusiness[business.id] === "check_error") {
      return "status_error";
    } else if (onboardingStatusPerBusiness[business.id] === "failed") {
      return "installation_failed";
    } else if (onboardingStatusPerBusiness[business.id] !== "completed") {
      return "onboarding";
    }
  }

  if (licensingEnabled && !user.is_demo) {
    if (isAllBusiness(business.id)) {
      if (!businesses.every((b) => b.licensing.web_app.licensed)) {
        return "unlicensed";
      }
    } else if (!business.licensing.web_app.licensed) {
      return "unlicensed";
    }
  }

  return "available";
}

export function useBusinessesStatusGetter(): (b: Business) => BusinessStatus {
  const onboardingProgress = useOnboardingProgress();
  const businesses = useGetBusinesses();
  const licensingFeatureFlag = useGetLicensingFeatureFlag();
  const licensingEnabled = licensingFeatureFlag.data?.enabled ?? false;
  const user = useSelf();

  return React.useCallback(
    (business: Business) => {
      if (!user.data) {
        return "undetermined";
      }
      return getBusinessStatus(
        licensingEnabled,
        business,
        onboardingProgress,
        businesses.data ?? [],
        user.data,
      );
    },
    [businesses.data, onboardingProgress, licensingEnabled, user.data],
  );
}

export function useGetBusinessStatus(business: Business): BusinessStatus {
  const businessStatusGetter = useBusinessesStatusGetter();
  return React.useMemo(() => {
    return businessStatusGetter(business);
  }, [businessStatusGetter, business]);
}
